import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { colorVars, fontWeightVars, radiusVars, spacingVars, typeScaleVars } from '@astryxdesign/core/theme/tokens.stylex';
import { Pagination } from '@astryxdesign/core/Pagination';
import { Selector } from '@astryxdesign/core/Selector';
import { Table, pixel, proportional, useTablePagination, useTableSortable, useTableStickyColumns } from '@astryxdesign/core/Table';
import type { ColumnWidth } from '@astryxdesign/core/Table';
import type { TableColumn } from '@astryxdesign/core/Table';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { VStack } from '@astryxdesign/core/VStack';
import { useFlip } from './motion/useFlip';
import { useElementWidth } from './useElementWidth';
import { defaultPageSize, pageSizes, sortRows } from './tableSort';
import { minPerShare, relaxedMinWidth, share, shouldStack, tableMinWidth } from './tableLayout';
import type { Width } from './tableLayout';
export interface DataColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  align?: 'start' | 'end' | 'center';
  width?: number | {
    flex: number;
  };
  minWidth?: number;
  sortable?: boolean;
  sortValue?: (row: T) => string | number | boolean | null | undefined;
}
export interface DataSort {
  key: string;
  direction: 'asc' | 'desc';
}
export interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  sort?: DataSort | null;
  onSortChange?: (sort: DataSort | null) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  paginationLabel?: string;
  onRowActivate?: (row: T) => void;
  loading?: boolean;
  density?: 'compact' | 'balanced';
  emptyText?: string;
}
export interface DataTableLabels {
  pagination: string;
  sort: string;
  ascending: string;
  descending: string;
}
const LabelsContext = createContext<DataTableLabels | null>(null);
export function columnSortable<T>(column: DataColumn<T>, controlled: boolean): boolean {
  return controlled ? Boolean(column.sortable) : Boolean(column.sortValue);
}
export const DataTableLabelsProvider = LabelsContext.Provider;
const longFieldChars = 22;
type Wrapped<T> = Record<string, unknown> & {
  __row: T;
  __key: string;
};
export function resolveWidth(width: Width, compact: boolean, minWidth?: number): ColumnWidth {
  if (typeof width === 'number' && !compact) return pixel(width);
  return proportional(share(width), {
    minWidth: minWidth ?? Math.round(share(width) * minPerShare)
  });
}
export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    rows,
    rowKey,
    sort,
    onSortChange,
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    paginationLabel,
    onRowActivate,
    loading,
    density = 'compact'
  } = props;
  const sortControlled = Boolean(onSortChange);
  const [ownSort, setOwnSort] = useState<DataSort | null>(null);
  const [ownPage, setOwnPage] = useState(1);
  const [ownSize, setOwnSize] = useState(defaultPageSize);
  const activeSort = sortControlled ? sort ?? null : ownSort;
  const isSortable = (c: DataColumn<T>) => columnSortable(c, sortControlled);
  const changeSort = (next: DataSort | null) => {
    setOwnPage(1);
    if (onSortChange) onSortChange(next);else setOwnSort(next);
  };
  const sortedRows = useMemo(() => {
    if (sortControlled || !ownSort) return rows;
    const column = columns.find(c => c.key === ownSort.key);
    return column?.sortValue ? sortRows(rows, column.sortValue, ownSort.direction) : rows;
  }, [rows, columns, ownSort, sortControlled]);
  const pageControlled = Boolean(onPageChange);
  const size = pageControlled ? pageSize ?? Math.max(rows.length, 1) : ownSize;
  const totalItems = pageControlled ? total ?? rows.length : sortedRows.length;
  const pages = Math.max(1, Math.ceil(totalItems / size));
  const currentPage = pageControlled ? page ?? 1 : Math.min(ownPage, pages);
  const visibleRows = pageControlled ? sortedRows : sortedRows.slice((currentPage - 1) * size, currentPage * size);
  const changePage = (next: number) => onPageChange ? onPageChange(next) : setOwnPage(next);
  const sizeChange = onPageSizeChange ?? (pageControlled ? undefined : (next: number) => {
    setOwnSize(next);
    setOwnPage(1);
  });
  const showPager = sizeChange ? totalItems > (pageSizes[0] ?? defaultPageSize) : totalItems > size;
  const labels = useContext(LabelsContext);
  const container = useRef<HTMLDivElement>(null);
  const [measure, width] = useElementWidth<HTMLDivElement>();
  const attach = useCallback((el: HTMLDivElement | null) => {
    container.current = el;
    measure(el);
  }, [measure]);
  const data = useMemo<Wrapped<T>[]>(() => visibleRows.map(row => ({
    __row: row,
    __key: rowKey(row)
  })), [visibleRows, rowKey]);
  const signature = useMemo(() => data.map(d => d.__key).join('|'), [data]);
  const widths = columns.map(c => c.width);
  const stacked = shouldStack(width, widths);
  const compact = width !== null && width < relaxedMinWidth(widths);
  const overflowing = !stacked && width !== null && width < tableMinWidth(columns);
  useFlip(container, stacked ? 'li' : 'tbody tr', signature);
  useLayoutEffect(() => {
    if (!stacked) return;
    container.current?.querySelectorAll<HTMLElement>('[data-qs-field]').forEach(field => {
      const long = (field.querySelector('dd')?.textContent?.trim().length ?? 0) > longFieldChars;
      field.style.gridColumn = long ? '1 / -1' : '';
    });
  }, [stacked, data, columns]);
  const tableColumns = useMemo<TableColumn<Wrapped<T>>[]>(() => columns.map(c => ({
    key: c.key,
    header: c.header,
    align: c.align ?? 'start',
    width: resolveWidth(c.width, compact, c.minWidth),
    sortable: columnSortable(c, sortControlled),
    renderCell: (item: Wrapped<T>) => onRowActivate && c === columns[0] ? <span role="link" tabIndex={0} {...{
      0: {
        className: "x1ypdohk"
      },
      1: {
        className: "x1ypdohk x1lliihq xb3r6kr xlyipyv xuxw1ft"
      }
    }[!!overflowing << 0]} onClick={() => onRowActivate(item.__row)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowActivate(item.__row);
      }
    }}>
              {c.render(item.__row)}
            </span> : overflowing && c === columns[0] ? <span className="x1lliihq xb3r6kr xlyipyv xuxw1ft">{c.render(item.__row)}</span> : c.render(item.__row)
  })), [columns, onRowActivate, compact, overflowing, sortControlled]);
  const sortPlugin = useTableSortable({
    sort: activeSort ? [{
      sortKey: activeSort.key,
      direction: activeSort.direction === 'asc' ? 'ascending' : 'descending'
    }] : [],
    onSortChange: next => {
      const first = next[0];
      changeSort(first ? {
        key: String(first.sortKey),
        direction: first.direction === 'ascending' ? 'asc' : 'desc'
      } : null);
    }
  });
  const paginationPlugin = useTablePagination({
    page: currentPage,
    onPageChange: changePage,
    pageSize: size,
    totalItems,
    variant: 'count',
    align: 'end',
    ...(sizeChange ? {
      pageSizeOptions: pageSizes,
      onPageSizeChange: sizeChange
    } : {}),
    ...(paginationLabel ?? labels?.pagination ? {
      label: paginationLabel ?? labels?.pagination
    } : {})
  });
  const anySortable = columns.some(isSortable);
  const stickyPlugin = useTableStickyColumns(overflowing && columns[0] ? {
    startKeys: [columns[0].key]
  } : {});
  const plugins = useMemo(() => {
    const out: Record<string, unknown> = {};
    if (overflowing) out.sticky = stickyPlugin;
    if (anySortable) out.sort = sortPlugin;
    if (showPager) out.pagination = paginationPlugin;
    return out;
  }, [overflowing, stickyPlugin, anySortable, sortPlugin, showPager, paginationPlugin]);
  if (loading && !rows.length) {
    return <VStack gap={2}>
        {Array.from({
        length: Math.min(pageSize ?? 6, 8)
      }, (_, i) => <Skeleton key={i} height={28} />)}
      </VStack>;
  }
  if (stacked) {
    const [head, ...rest] = columns;
    const sortable = labels ? columns.filter(c => isSortable(c) && c.header) : [];
    const activate = (row: T) => onRowActivate?.(row);
    return <div ref={attach} className="xeuugli xh8yej3">
        {sortable.length ? <div className="x78zum5 x13a6bvl xy143xn">
            <Selector label={labels?.sort ?? ''} placeholder={labels?.sort ?? ''} isLabelHidden size="sm" variant="ghost" value={activeSort ? `${activeSort.key}:${activeSort.direction}` : ''} onChange={v => {
          const [key, direction] = String(v ?? '').split(':');
          changeSort(key && (direction === 'asc' || direction === 'desc') ? {
            key,
            direction
          } : null);
        }} options={sortable.flatMap(c => [{
          value: `${c.key}:desc`,
          label: `${c.header} · ${labels?.descending ?? ''}`
        }, {
          value: `${c.key}:asc`,
          label: `${c.header} · ${labels?.ascending ?? ''}`
        }])} />
          </div> : null}
        <ul className="x3ct3a4 x1ghz6dp x1717udv x78zum5 xdt5ytf x1txdalj">
          {data.map(item => <li key={item.__key} className="x78zum5 xdt5ytf x1txdalj xeuugli x8o8v82 xrrkdod xh6dtrn xwmxj5m xmkeg23 x1y0btm7 x14i3s5s">
              {head ? <div {...{
            0: {
              className: "xeuugli x2mo6ok xj0a0fe"
            },
            1: {
              className: "xeuugli x2mo6ok xj0a0fe x1ypdohk"
            }
          }[!!Boolean(onRowActivate) << 0]} {...onRowActivate ? {
            role: 'link',
            tabIndex: 0,
            onClick: () => activate(item.__row),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate(item.__row);
              }
            }
          } : {}}>
                  {head.render(item.__row)}
                </div> : null}
              <dl className="xrvj5dj x1aa5uif xewh9hi x1x7z4sm x1ghz6dp">
                {rest.filter(c => c.header).map(c => <div key={c.key} data-qs-field="" className="x78zum5 xdt5ytf x195vfkc xeuugli">
                      <dt className="x1ghz6dp xeuugli xv1l7n4 x141an7d x1ltkj2j xj0a0fe">{c.header}</dt>
                      <dd className="x1ghz6dp xeuugli xj0a0fe x1e4wzip xss6m8b">{c.render(item.__row)}</dd>
                    </div>)}
              </dl>
              {rest.some(c => !c.header) ? <div className="x78zum5 x1a02dak x1txdalj">
                  {rest.filter(c => !c.header).map(c => <span key={c.key}>{c.render(item.__row)}</span>)}
                </div> : null}
            </li>)}
        </ul>
        {showPager ? <div className="x78zum5 xl56j7k x1xye8es">
            <Pagination page={currentPage} onChange={changePage} totalItems={totalItems} pageSize={size} {...sizeChange ? {
          pageSizeOptions: pageSizes,
          onPageSizeChange: sizeChange
        } : {}} variant="compact" size="sm" {...paginationLabel ?? labels?.pagination ? {
          label: paginationLabel ?? labels?.pagination
        } : {}} />
          </div> : null}
      </div>;
  }
  return <div ref={attach} {...overflowing ? {
    'data-scroll': 'x'
  } : {}} className="xeuugli xh8yej3">
      <Table data={data} columns={tableColumns} idKey="__key" density={density} hasHover textOverflow="truncate" plugins={plugins as never} />
    </div>;
}